import { FunctionComponent, h } from "preact"
import { useEffect, useRef } from "preact/hooks"

import { ApiResponse } from "../../../types/api-response"
import { Button } from "../../../parts/buttons"
import { KLHKResponse } from "../../../hooks/useAllklhk"
import TopBar from "../../../components/topbar"
import { mutate } from "swr"
import useAuth from "../../../hooks/useAuth"
import useCems from "../../../hooks/useCems"
import useMills from "../../../hooks/useMills"
import useObjectState from "../../../hooks/useObjectState"

type AddKLHKProps = h.JSX.HTMLAttributes<HTMLDivElement> & {
  onBack?: () => void
  uuid?: string
  editAt?: number
}

const defaultBody = {
  mil: { uuid: "" },
  cem: { uuid: "" },
  area: "-",
  parameter: "",
  description: "",
  completion_date: "",
  file: "",
}

const AddKLHK: FunctionComponent<AddKLHKProps> = ({
  onBack,
  uuid: klhkId,
  editAt,
}) => {
  const [body, setBody] = useObjectState(defaultBody)
  const { data: mills } = useMills()
  const { data: cems } = useCems()
  const { fetcher } = useAuth()

  const file = useRef<HTMLInputElement>(null)

  const handleSubmit = async () => {
    if (!klhkId) {
      const form = new FormData()
      if (file.current?.files?.length)
        form.append("file", file.current?.files[0])
      fetcher()
        .post("/manual-report/file", form, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(({ data }) => data?.data?.path as string)
        .then((path) => {
          return fetcher().post("/ncr-klhk", { ...body, file: path || "" })
        })
        .catch(console.error)
        .finally(() => {
          mutate("/ncr-klhk")
          if (onBack) onBack()
        })
    } else {
      let path = body.file
      if (file.current?.files?.length) {
        const form = new FormData()
        form.append("file", file.current?.files[0])
        path = await fetcher()
          .post("/manual-report/file", form, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(({ data }) => data?.data?.path as string)
      }
      fetcher()
        .patch("/ncr-klhk/" + klhkId, { ...body, file: path || body.file })
        .catch(console.error)
        .finally(() => {
          mutate("/ncr-klhk")
          if (onBack) onBack()
        })
    }
  }

  useEffect(() => {
    if (klhkId)
      fetcher()
        .get<ApiResponse<KLHKResponse>>("/ncr-klhk/" + klhkId)
        .then(({ data }) => data.data)
        .then((data) => {
          setBody({
            mil: { uuid: data.mil.uuid },
            cem: { uuid: data.cem.uuid },
            area: data.area,
            completion_date: data.completion_date,
            description: data.description,
            file: data.file,
            parameter: data.parameter,
          })
        })
        .catch(console.error)
  }, [editAt, klhkId])

  return (
    <div>
      <TopBar title="SPARING" subtitle="KLHK" onBack={onBack} />
      <div className="p-3">
        <form
          action=""
          onSubmit={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
        >
          <div className="card">
            <div className="card-body h5">{klhkId ? "Edi" : "Add"} KLHK</div>
            <div className="card-body">
              <div className="mb-2">
                <label>Mills</label>
                <select
                  required
                  value={body.mil.uuid}
                  className="form-control rounded-pill"
                  onChange={({ currentTarget: e }) =>
                    setBody({ mil: { uuid: e.value } })
                  }
                >
                  {mills?.map((mil) => (
                    <option value={mil.uuid} key={mil.uuid}>
                      {mil.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-2">
                <label>Unit</label>
                <select
                  required
                  value={body.cem.uuid}
                  className="form-control rounded-pill"
                  onChange={({ currentTarget: e }) =>
                    setBody({ cem: { uuid: e.value } })
                  }
                >
                  {cems
                    ?.filter(({ mil }) => mil.uuid == body.mil.uuid)
                    .map((cem) => (
                      <option value={cem.uuid} key={cem.uuid}>
                        {cem.name}
                      </option>
                    ))}
                </select>
              </div>
              {/* <div className="mb-2">
                <label>Area</label>
                <select
                  required
                  value={body.area}
                  className="form-control rounded-pill"
                  onChange={({ currentTarget: e }) =>
                    setBody({ area: e.value })
                  }
                >
                  <option value="Power Plant">Power Plant</option>
                  <option value="Non Power Plant">Non Power Plant</option>
                </select>
              </div> */}
              <div className="mb-2">
                <label>Parameter</label>
                <select
                  required
                  value={body.parameter}
                  className="form-control rounded-pill"
                  onChange={({ currentTarget: e }) =>
                    setBody({ parameter: e.value })
                  }
                >
                  {cems
                    ?.find(({ uuid }) => uuid == body.cem.uuid)
                    ?.parameters.map((param) => (
                      <option value={param.name} key={param.uuid}>
                        {param.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="mb-2">
                <label>Problem Description</label>
                <textarea
                  required
                  className="form-control"
                  rows={3}
                  style={{ maxWidth: "100%", maxHeight:"560px"  }}
                  value={body.description}
                  onChange={({ currentTarget: e }) =>
                    setBody({ description: e.value })
                  }
                ></textarea>
                {/* <input
                  type="text"
                  required
                  className="form-control"
                  value={body.description}
                  onChange={({ currentTarget: e }) =>
                    setBody({ description: e.value })
                  }
                  rows={3}
                  style={{ maxWidth: "100%", maxHeight:"560px"  }}
                /> */}
              </div>
              <div className="mb-2">
                <label>Completion Date</label>
                <input
                  type="date"
                  required
                  className="form-control"
                  value={body.completion_date}
                  onChange={({ currentTarget: e }) =>
                    setBody({ completion_date: e.value })
                  }
                />
              </div>
              <div className="mb-2">
                <label>Upload KLHK Report</label>
                <input
                  ref={file}
                  type="file"
                  required={klhkId ? false : true}
                  className="form-control"
                />
              </div>
            </div>
            <div className="card-body d-flex justify-content-end">
              <Button type="submit" className="mx-1" variant="primary">
                {klhkId ? "Save" : "Add Report"}
              </Button>
              <Button
                onClick={onBack}
                type="button"
                className="mx-1"
                variant="tertiary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddKLHK
